import { ExamType, RegistrationStatus, RegistrationType } from '@exam-center/graphql';
import {
  BusinessOutlinedIcon,
  DescriptionOutlinedIcon,
  DesktopWindowsOutlinedIcon,
  PublicIcon,
  WarningRoundedIcon,
} from '@shared/components';
import { dayjs } from '@shared/utils';

import { type ExamEventData } from '../providers';
import { formatRoomData } from '../utils';

export function getLabelAndIconByExamType(examType: ExamType) {
  switch (examType) {
    case ExamType.Computer:
      return { icon: <DesktopWindowsOutlinedIcon />, label: 'Computer', checked: true };
    case ExamType.Paper:
      return { icon: <DescriptionOutlinedIcon />, label: 'Paper', checked: true };
    default:
      return {
        icon: <WarningRoundedIcon />,
        label: 'Unknown',
        checked: false,
      };
  }
}

export function getLabelAndIconByRegistrationType(registrationType: RegistrationType) {
  switch (registrationType) {
    case RegistrationType.Public:
      return { icon: <PublicIcon />, label: 'Public', checked: true };
    case RegistrationType.InHouse:
      return { icon: <BusinessOutlinedIcon />, label: 'In-house', checked: true };
    default:
      return {
        icon: <WarningRoundedIcon />,
        label: 'Unknown',
        checked: false,
      };
  }
}

export function formatProctorFullName(proctor: {
  titleTh: string;
  firstNameTh: string;
  lastNameTh: string;
  middleNameTh: string | null;
}) {
  return [proctor.titleTh, proctor.firstNameTh, proctor.middleNameTh, proctor.lastNameTh]
    .filter(Boolean)
    .join(' ');
}

export function getRegistrationStatusByCurrentStatus(input: {
  currentStatus: RegistrationStatus;
  isRegistrationStartedAtPassed: boolean;
}) {
  const { currentStatus, isRegistrationStartedAtPassed } = input;

  switch (currentStatus) {
    case RegistrationStatus.OpenForRegistration:
      return [
        RegistrationStatus.OpenForRegistration,
        RegistrationStatus.TemporarilyClosed,
        RegistrationStatus.Canceled,
      ];
    case RegistrationStatus.TemporarilyClosed:
      if (isRegistrationStartedAtPassed) {
        return [
          RegistrationStatus.OpenForRegistration,
          RegistrationStatus.TemporarilyClosed,
          RegistrationStatus.Canceled,
        ];
      }
      return [
        RegistrationStatus.Scheduled,
        RegistrationStatus.TemporarilyClosed,
        RegistrationStatus.Canceled,
      ];

    case RegistrationStatus.SessionFull:
      return [
        RegistrationStatus.SessionFull,
        RegistrationStatus.TemporarilyClosed,
        RegistrationStatus.Canceled,
      ];
    case RegistrationStatus.RegistrationClosed:
      return [RegistrationStatus.RegistrationClosed, RegistrationStatus.Canceled];
    case RegistrationStatus.Scheduled:
      return [
        RegistrationStatus.Scheduled,
        RegistrationStatus.TemporarilyClosed,
        RegistrationStatus.Canceled,
      ];
    default:
      return [currentStatus];
  }
}

export function getExamEventDetailsData(examEvent: ExamEventData) {
  const examType = [getLabelAndIconByExamType(examEvent.examType)];
  const registrationType = [getLabelAndIconByRegistrationType(examEvent.registrationType)];

  const room = examEvent.rooms?.map(formatRoomData);
  const mainProctors = examEvent.proctors?.map(formatProctorFullName) ?? [];
  const isRegistrationStartedAtPassed = dayjs().isAfter(dayjs(examEvent.registrationStartedAt));
  const registrationStatusOptions = getRegistrationStatusByCurrentStatus({
    currentStatus: examEvent.registrationStatus,
    isRegistrationStartedAtPassed: isRegistrationStartedAtPassed,
  });

  const examCourses = examEvent.courses
    .map((course) => course.examCourse?.name ?? null)
    .filter((name) => name !== null);

  return {
    examCourses,
    examType,
    registrationType,
    room,
    mainProctors,
    registrationStatusOptions,
  };
}

export function transformRoomsToSelected(
  rooms:
    | {
        id: string;
      }[]
    | null,
): Record<string, true> {
  if (!rooms) return {};
  return rooms.reduce<Record<string, true>>((acc, room) => {
    acc[room.id] = true;
    return acc;
  }, {});
}

export function transformToLabelValueOptions(item: { id: string; nameTh: string }) {
  return {
    label: item.nameTh,
    value: item.id,
  };
}
